/**
 * The programme audio mix: what the broadcast hears, as opposed to what each
 * participant's own microphone is doing.
 *
 * These are two different powers and conflating them was a mistake worth
 * naming. Muting a *device* through LiveKit reaches into someone's browser and
 * switches their microphone off - they see it, and LiveKit refuses to undo it
 * remotely by default. Muting a *channel* here touches no one's device: it
 * only decides whether that audio reaches the output. A director needs the
 * second one, and needs to be able to take it back.
 *
 * The shape is the one `phase-08.md` specifies, so the full mixer extends this
 * rather than replacing it. Only `muted` is driven by the UI today; `volume`
 * and `solo` are honoured by the renderer and wait for Phase 8's faders.
 */

export interface AudioChannelState {
  /** 0 to 1.5, as phase-08.md's 0-150% fader. */
  volume: number;
  muted: boolean;
  solo: boolean;
}

export interface AudioMasterState {
  volume: number;
  muted: boolean;
}

export interface ProgramAudio {
  /** Keyed by `audioChannelId`. A source with no entry is live at full gain. */
  channels: Record<string, AudioChannelState>;
  master: AudioMasterState;
}

export const DEFAULT_AUDIO_CHANNEL: AudioChannelState = {
  volume: 1,
  muted: false,
  solo: false,
};

export const DEFAULT_PROGRAM_AUDIO: ProgramAudio = {
  channels: {},
  master: { volume: 1, muted: false },
};

/**
 * A channel's stable identity.
 *
 * `phase-08.md` keys channels by track id, and that is the obvious choice
 * until a track is republished: the sid changes, the entry is orphaned, and a
 * source the director had taken off air comes back **live**. A hot microphone
 * is the one failure this must not have, so the key is built from the
 * participant and the track name instead, both of which survive a republish.
 */
export function audioChannelId(participantIdentity: string, trackName: string | null): string {
  return `${participantIdentity}::${trackName ?? ''}`;
}

/**
 * The gain a channel should actually play at, having applied its own state,
 * any solo elsewhere in the mix, and the master.
 *
 * Solo is exclusive by nature: the moment one channel is soloed every channel
 * that is not becomes silent, which is why this cannot be decided by looking
 * at one channel alone.
 */
export function effectiveGain(audio: ProgramAudio, channelId: string): number {
  const channel = audio.channels[channelId] ?? DEFAULT_AUDIO_CHANNEL;
  if (audio.master.muted || channel.muted) {
    return 0;
  }

  const anySolo = Object.values(audio.channels).some((candidate) => candidate.solo);
  if (anySolo && !channel.solo) {
    return 0;
  }

  return Math.max(0, channel.volume) * Math.max(0, audio.master.volume);
}

/** Whether the director has taken this channel off air. */
export function isChannelMuted(audio: ProgramAudio, channelId: string): boolean {
  return audio.channels[channelId]?.muted ?? false;
}
